/**
 * O histórico das Novidades (spec 036): uma seção por mês, as entradas
 * numeradas dentro dele, com o texto inteiro, as imagens e a ação — como a
 * página de atualizações do TiFlux. Mostra tudo o que a pessoa pode ver (D3),
 * e abrir a página conta como ter visto (D2). `/novidades#novidade-<id>` leva
 * direto à entrada (o "Ver no histórico" do painel).
 */
import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/misc';
import { NewsBody, NewsImages, NewsText } from './news-content';
import { useNews } from './news-queries';
import { useRunNewsAction } from './NewsMegaphone';
import { dayHeading, groupByMonth, newsAnchorId, type NewsAudience } from './news-model';

export function NewsPage({ audience }: { audience: NewsAudience }) {
  const news = useNews(audience);
  const runAction = useRunNewsAction();
  const hash = useRouterState({ select: (s) => s.location.hash });
  const { visible, markSeen } = news;

  // abrir a página é ver o que ela mostra
  useEffect(() => {
    if (visible.length > 0) markSeen(visible.map((e) => e.id));
  }, [visible, markSeen]);

  // a entrada pedida pelo painel
  useEffect(() => {
    if (!hash || visible.length === 0) return;
    document.getElementById(hash)?.scrollIntoView({ block: 'start' });
  }, [hash, visible.length]);

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-foreground">Novidades</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          O que mudou no ERP, mês a mês: o antes, o depois e onde clicar.
        </p>
      </header>
      {visible.length === 0 ? (
        <EmptyState message="Nenhuma novidade por aqui ainda." />
      ) : (
        groupByMonth(visible).map((month) => (
          <section key={month.key} aria-labelledby={`mes-${month.key}`} className="mb-10">
            <h2 id={`mes-${month.key}`} className="mb-4 border-b pb-2 text-lg font-semibold text-foreground">
              {month.heading}
            </h2>
            <ol className="space-y-8">
              {month.entries.map((entry, i) => (
                <li key={entry.id} id={newsAnchorId(entry.id)} className="scroll-mt-4">
                  <article>
                    <p className="text-xs font-medium text-muted-foreground">{dayHeading(entry.date)}</p>
                    <h3 className="mt-0.5 text-base font-semibold text-foreground">
                      {`${i + 1}. `}
                      <NewsText text={entry.title} vars={news.vars} />
                    </h3>
                    <NewsBody blocks={entry.body} vars={news.vars} className="mt-2" />
                    {entry.images?.length ? (
                      <div className="mt-3 max-w-xl">
                        <NewsImages images={entry.images} vars={news.vars} />
                      </div>
                    ) : null}
                    {entry.action ? (
                      <div className="mt-3 flex items-center gap-3">
                        <Button size="sm" variant="outline" onClick={() => runAction(entry)}>
                          {entry.action.label}
                        </Button>
                        {entry.tour && news.toursDone.has(entry.tour.id) ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <CheckCircle2 className="size-3.5" aria-hidden />
                            Tour feito
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </article>
                </li>
              ))}
            </ol>
          </section>
        ))
      )}
    </div>
  );
}

export function StaffNewsPage() {
  return <NewsPage audience="staff" />;
}

export function PortalNewsPage() {
  return <NewsPage audience="portal" />;
}
